import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { cn } from '@/lib/utils'
import { engine } from '@/lib/motion/engine'
import { paintLandscape } from '@/lib/procedural/landscapes'
import { whenIdle } from '@/lib/motion/idle'
import { getAppState, subscribeAppState } from '@/lib/app-store'
import { solutions, specs, stepWindows } from '@/constants/content'

import {
    clamp,
    easeOutCubic,
    lerp,
    mixHex,
    range,
    smoothstep,
    stepped
} from '@/lib/motion/math'

import {
    emptyMetrics,
    enterProgress,
    isNear,
    measureSection,
    pinRaw,
    useEngine
} from '@/lib/motion/section'

import {
    Bracket,
    MaskTitle,
    RevealWords,
    createMaskDriver,
    createStyleCache,
    createWordReveal
} from '@/components/landing/primitives'

const TAB_TARGETS = [0.1, 0.375, 0.625, 0.9]

const SolutionsSection: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const frameRef = useRef<HTMLDivElement>(null)
    const titleRef = useRef<HTMLHeadingElement>(null)
    const tabsRef = useRef<HTMLDivElement>(null)
    const underlineRef = useRef<HTMLSpanElement>(null)
    const counterRef = useRef<HTMLSpanElement>(null)
    const panelRef = useRef<HTMLDivElement>(null)
    const wipeRef = useRef<HTMLDivElement>(null)
    const specsRef = useRef<HTMLDListElement>(null)
    const bodyRef = useRef<HTMLDivElement>(null)
    const tabRefs = useRef<Array<HTMLButtonElement | null>>([])
    const copyRefs = useRef<Array<HTMLDivElement | null>>([])
    const followRefs = useRef<Array<HTMLSpanElement | null>>([])
    const canvasRefs = useRef<Array<HTMLCanvasElement | null>>([])
    const chipRefs = useRef<Array<HTMLUListElement | null>>([])
    const captionRefs = useRef<Array<HTMLSpanElement | null>>([])
    const valueRefs = useRef<Array<HTMLSpanElement | null>>([])

    useEngine(() => {
        const set = createStyleCache()
        const mask = createMaskDriver(titleRef.current)
        const follows = solutions.map((_, index) => createWordReveal(followRefs.current[index], 'fade', 2))
        const lastValues = specs.map(() => -1)
        const timers: Array<number> = []

        let metrics = emptyMetrics()
        let tabLeft: Array<number> = []
        let tabWidth: Array<number> = []
        let painted = ''
        let active = -1

        const paintAll = (): void => {
            timers.forEach(id => window.clearTimeout(id))
            timers.length = 0

            solutions.forEach((solution, index) => {
                timers.push(window.setTimeout(() => {
                    whenIdle(() => {
                        const canvas = canvasRefs.current[index]

                        if (canvas) paintLandscape(canvas, solution.id)
                    })
                }, 60 + index * 160))
            })
        }

        const paintWhenReady = (): void => {
            const panel = panelRef.current

            if (painted || !panel) return

            painted = `${panel.clientWidth}x${panel.clientHeight}`
            paintAll()
        }

        let introTimer = 0

        const offLoaded = subscribeAppState(() => {
            if (getAppState().loaded && !introTimer) introTimer = window.setTimeout(paintWhenReady, 3200)
        })

        if (getAppState().loaded) introTimer = window.setTimeout(paintWhenReady, 3200)

        return {
            measure: () => {
                if (sectionRef.current) metrics = measureSection(sectionRef.current, frameRef.current)

                tabLeft = tabRefs.current.map(tab => tab?.offsetLeft ?? 0)
                tabWidth = tabRefs.current.map(tab => tab?.offsetWidth ?? 0)

                const panel = panelRef.current

                if (panel && painted) {
                    const size = `${panel.clientWidth}x${panel.clientHeight}`

                    if (size !== painted) {
                        painted = size
                        paintAll()
                    }
                }
            },
            frame: (state) => {
                const panel = panelRef.current

                if (!painted && panel && isNear(metrics, state.scroll, state.vh, 2.5)) paintWhenReady()

                if (!isNear(metrics, state.scroll, state.vh)) return

                const enter = enterProgress(metrics, state.scroll, state.vh)
                const raw = pinRaw(metrics, state.scroll)
                const p = clamp(raw)
                const s = stepped(p, stepWindows.solutions)
                const current = Math.min(3, Math.max(0, Math.round(s)))
                const from = Math.min(2, Math.floor(s))
                const f = clamp(s - from)

                mask(smoothstep(0.3, 1, enter))

                set(bodyRef.current, 'opacity', smoothstep(0.45, 0.95, enter).toFixed(3))

                tabRefs.current.forEach((tab, index) => {
                    set(tab, 'color', mixHex('#9aa3a8', '#1d2a31', clamp(1 - Math.abs(s - index))))
                })

                if (tabLeft.length === solutions.length) {
                    const left = lerp(tabLeft[from], tabLeft[from + 1], f)
                    const width = lerp(tabWidth[from], tabWidth[from + 1], f)

                    set(underlineRef.current, 'transform', `translate3d(${left.toFixed(2)}px, 0, 0) scaleX(${width.toFixed(2)})`)
                }

                copyRefs.current.forEach((copy, index) => {
                    const d = s - index

                    set(copy, 'opacity', clamp(1 - Math.abs(d) * 2.4).toFixed(3))
                    set(copy, 'transform', `translate3d(0, ${(-d * 26).toFixed(1)}px, 0)`)
                    set(copy, 'visibility', Math.abs(d) < 0.5 ? 'visible' : 'hidden')
                })

                chipRefs.current.forEach((row, index) => {
                    const d = s - index

                    set(row, 'opacity', clamp(1 - Math.abs(d) * 2.4).toFixed(3))
                    set(row, 'transform', `translate3d(0, ${(-d * 14).toFixed(1)}px, 0)`)
                    set(row, 'visibility', Math.abs(d) < 0.5 ? 'visible' : 'hidden')
                })

                captionRefs.current.forEach((caption, index) => {
                    set(caption, 'opacity', clamp(1 - Math.abs(s - index) * 2.4).toFixed(3))
                })

                follows.forEach((reveal, index) => {
                    if (index === 0) {
                        reveal(range(-0.16, 0.1, raw))

                        return
                    }

                    const end = stepWindows.solutions[index - 1][1]

                    reveal(range(end - 0.02, end + 0.11, raw))
                })

                canvasRefs.current.forEach((canvas, index) => {
                    if (index === 0) return

                    const wipe = clamp(s - (index - 1))

                    set(canvas, 'clip-path', `inset(${((1 - wipe) * 100).toFixed(3)}% 0% 0% 0%)`)
                })

                const wiping = f > 0.002 && f < 0.998 && s < 3

                set(wipeRef.current, 'opacity', wiping ? '1' : '0')
                set(wipeRef.current, 'transform', `translate3d(0, ${((1 - f) * 100).toFixed(3)}%, 0)`)

                const counting = easeOutCubic(range(0.5, 1, enter))

                specs.forEach((spec, index) => {
                    const value = Math.round(spec.value * counting)

                    if (value === lastValues[index]) return

                    lastValues[index] = value

                    const element = valueRefs.current[index]

                    if (element) element.textContent = value.toLocaleString('en-US')
                })

                if (current !== active) {
                    active = current

                    tabRefs.current.forEach((tab, index) => {
                        tab?.setAttribute('aria-selected', index === current ? 'true' : 'false')
                    })

                    if (counterRef.current) counterRef.current.textContent = `0${current + 1}`
                }
            },
            cleanup: () => {
                offLoaded()
                window.clearTimeout(introTimer)
                timers.forEach(id => window.clearTimeout(id))
            }
        }
    })

    const goToTab = (index: number): void => {
        const section = sectionRef.current
        const frame = frameRef.current

        if (!section || !frame) return

        const top = section.getBoundingClientRect().top + window.scrollY
        const length = Math.max(1, section.offsetHeight - frame.offsetHeight)

        engine.scrollTo(top + length * TAB_TARGETS[index], { duration: 1.6 })
    }

    return (
        <section id='solutions' ref={sectionRef} aria-labelledby='solutions-title' className='relative h-[400vh] md:h-[440vh]'>
            <div ref={frameRef} data-frame='' className='sticky top-0 flex h-svh flex-col overflow-hidden px-4 pb-5 pt-[68px] md:px-10 md:pb-8 md:pt-[72px]'>
                <h2 id='solutions-title' ref={titleRef} className='display-giant shrink-0 text-[clamp(60px,min(14.5vw,22vh),250px)] text-ink'>
                    <MaskTitle>Solutions</MaskTitle>
                </h2>

                <div ref={bodyRef} className='flex min-h-0 flex-1 flex-col'>
                    <div className='relative mt-3 flex items-center justify-between border-b border-line md:mt-5'>
                        <div ref={tabsRef} role='tablist' aria-label='Industries' className='no-scrollbar relative flex gap-5 overflow-x-auto md:gap-10'>
                            {solutions.map((solution, index) => (
                                <button
                                    key={solution.id}
                                    ref={(element) => { tabRefs.current[index] = element }}
                                    type='button'
                                    role='tab'
                                    aria-selected={index === 0}
                                    onClick={() => goToTab(index)}
                                    className='shrink-0 py-3 text-[14px] tracking-[-0.01em] md:py-4 md:text-[17px]'
                                    style={{ color: index === 0 ? '#1d2a31' : '#9aa3a8' }}
                                >
                                    {solution.label}
                                </button>
                            ))}

                            <span
                                ref={underlineRef}
                                aria-hidden='true'
                                className='pointer-events-none absolute bottom-0 left-0 h-[2px] w-px origin-left bg-ink'
                            />
                        </div>

                        <span className='label-mono hidden text-haze md:inline'>
                            <span ref={counterRef}>01</span> / 04
                        </span>
                    </div>

                    <div className='mt-4 grid min-h-0 flex-1 grid-rows-[auto_1fr_auto] gap-4 md:mt-8 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.5fr)_minmax(0,0.9fr)] md:grid-rows-1 md:gap-8 xl:gap-12'>
                        <div className='grid content-start'>
                            {solutions.map((solution, index) => (
                                <div
                                    key={solution.id}
                                    ref={(element) => { copyRefs.current[index] = element }}
                                    className='[grid-area:1/1]'
                                    style={{ opacity: index === 0 ? 1 : 0 }}
                                >
                                    <p className='text-[15px] leading-[1.45] tracking-[-0.01em] md:text-[clamp(17px,1.35vw,21px)]'>
                                        <span className='text-ink'>{solution.lead}</span>{' '}
                                        <span ref={(element) => { followRefs.current[index] = element }}>
                                            <RevealWords text={solution.follow} />
                                        </span>
                                    </p>
                                </div>
                            ))}
                        </div>

                        <div className='flex min-h-0 flex-col gap-3 md:gap-4'>
                            <div
                                ref={panelRef}
                                data-anchor='solutions-panel'
                                className='relative min-h-[180px] flex-1 overflow-hidden rounded-[20px] border border-line bg-[#e3eae6] md:max-h-[min(54vh,34vw)]'
                            >
                                {solutions.map((solution, index) => (
                                    <canvas
                                        key={solution.id}
                                        ref={(element) => { canvasRefs.current[index] = element }}
                                        aria-hidden='true'
                                        className='absolute inset-0 block size-full'
                                        style={{ clipPath: index === 0 ? 'none' : 'inset(100% 0% 0% 0%)' }}
                                    />
                                ))}

                                <div ref={wipeRef} aria-hidden='true' className='pointer-events-none absolute inset-0 opacity-0'>
                                    <div className='absolute inset-x-0 top-0 h-px bg-glacier shadow-[0_0_14px_3px_rgba(143,227,240,0.85)]' />
                                </div>

                                <div aria-hidden='true' className='pointer-events-none absolute inset-0 overflow-hidden'>
                                    <div className='scan-sweep absolute inset-x-0 top-0 h-[10%] bg-gradient-to-b from-transparent via-white/25 to-transparent' />
                                </div>

                                <div aria-hidden='true' className='pointer-events-none absolute inset-0'>
                                    <span className='absolute left-3 top-3 size-3 border-l border-t border-ink/50' />
                                    <span className='absolute right-3 top-3 size-3 border-r border-t border-ink/50' />
                                    <span className='absolute bottom-3 left-3 size-3 border-b border-l border-ink/50' />
                                    <span className='absolute bottom-3 right-3 size-3 border-b border-r border-ink/50' />
                                </div>

                                <div className='pointer-events-none absolute bottom-3.5 left-8 right-8 grid'>
                                    {solutions.map((solution, index) => (
                                        <span
                                            key={solution.id}
                                            ref={(element) => { captionRefs.current[index] = element }}
                                            className='label-mono truncate text-ink/75 [grid-area:1/1]'
                                            style={{ opacity: index === 0 ? 1 : 0 }}
                                        >
                                            {solution.caption}
                                        </span>
                                    ))}
                                </div>

                                <Bracket className='pointer-events-none absolute left-8 top-3.5 text-ink/75'>From above</Bracket>
                            </div>

                            <div className='grid'>
                                {solutions.map((solution, index) => (
                                    <ul
                                        key={solution.id}
                                        ref={(element) => { chipRefs.current[index] = element }}
                                        className='no-scrollbar flex gap-2 overflow-x-auto [grid-area:1/1]'
                                        style={{ opacity: index === 0 ? 1 : 0 }}
                                        aria-label={`${solution.label} applications`}
                                    >
                                        {solution.chips.map((chip, chipIndex) => (
                                            <li
                                                key={chip}
                                                className={cn(
                                                    'shrink-0 rounded-full border px-3.5 py-1.5 text-[12px] tracking-[-0.01em] md:text-[13px]',
                                                    chipIndex === solution.activeChip
                                                        ? 'border-ink bg-ink text-white'
                                                        : 'border-line bg-white/60 text-haze'
                                                )}
                                            >
                                                {chip}
                                            </li>
                                        ))}
                                    </ul>
                                ))}
                            </div>
                        </div>

                        <dl ref={specsRef} className='grid grid-cols-3 self-start border-l border-t border-line md:grid-cols-2 [@media(max-height:740px)]:max-md:hidden'>
                            {specs.map((spec, index) => (
                                <div key={spec.label} className='flex flex-col gap-1.5 border-b border-r border-line p-3 md:gap-3 md:p-4 xl:p-5'>
                                    <dt className='font-mono text-[9px] uppercase leading-[1.35] tracking-[0.1em] text-haze md:text-[10.5px]'>
                                        {spec.label}
                                    </dt>

                                    <dd className='flex items-baseline gap-1.5 text-ink'>
                                        <span className='text-[20px] font-light leading-none tracking-[-0.04em] tabular-nums md:text-[clamp(24px,2.2vw,36px)]'>
                                            {spec.prefix}
                                            <span ref={(element) => { valueRefs.current[index] = element }}>
                                                {spec.value.toLocaleString('en-US')}
                                            </span>
                                            {spec.suffix}
                                        </span>

                                        {spec.unit && (
                                            <span className='text-[11px] text-haze md:text-[13px]'>
                                                {spec.unit}
                                            </span>
                                        )}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default SolutionsSection
